import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Archive, ArchiveRestore, ExternalLink, HandHeart, Loader2, MoreHorizontal, Sparkles, Undo2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { profileHref } from "@/components/social/AuthorLink";
import { OtterAssistHelpButton } from "@/components/social/OtterAssistHelpButton";
import { needHref, postHref } from "@/lib/otterAssistRoutes";

// Otter Assist V1 Phase 6 — one delivered Community Need (OAD-068 / OAD-071 / OAD-073 / OAD-077 /
// OAD-078 / OAD-081). Deliberately the minimum functional card: poster, need summary, urgency and
// deadline, the immutable "Why you matched", New / Updated indicators, View Post, I Can Help,
// Archive / Restore and the two dispositions (with an explicit Undo for Not a Good Fit).
// Phase 7 owns search, filters, sorting and the richer management experience.

const URGENCY_COPY = {
  immediate: "Immediate — today", asap: "ASAP — within 48 hours", soon: "Soon — within 7 days",
  within_days: "Within a set number of days", flexible: "Flexible — no firm deadline",
};

export function CommunityNeedCard({ need, view }) {
  const qc = useQueryClient();
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["oa-community-needs"] });
    qc.invalidateQueries({ queryKey: ["oa-unread-count"] });
  };

  const act = useMutation({
    mutationFn: async (action) =>
      (await api.post(`/otter-assist/community-needs/${need.delivery_id}/action`, { action })).data,
    onSuccess: invalidate,
    onError: () => toast.error("That didn't work — please try again."),
  });

  const dispose = useMutation({
    mutationFn: async (action) =>
      (await api.post(`/otter-assist/assists/${need.assist_id}/disposition`, { action })).data,
    onSuccess: (_d, action) => {
      toast.success(action === "undo_not_a_good_fit"
        ? "Restored — this need is back in your inbox."
        : "Thanks — we've taken it out of your inbox.");
      invalidate();
    },
    onError: () => toast.error("That didn't work — please try again."),
  });

  // Genuine interaction with THIS need is what clears the New / Updated indicators — never merely
  // loading the list (OAD-071 / OAD-081).
  const markReviewed = () => {
    if (need.is_new) act.mutate("seen");
    if (need.is_updated) act.mutate("acknowledge_update");
  };

  const busy = act.isPending || dispose.isPending;
  const archived = view === "archived";

  return (
    <article className="rounded-xl border border-border bg-card p-4 transition-colors hover:border-teal-200"
      data-testid={`community-need-${need.delivery_id}`}>
      <div className="flex items-start gap-3">
        {need.poster?.headshot_url ? (
          <img src={need.poster.headshot_url} alt="" className="h-10 w-10 shrink-0 rounded-full object-cover" />
        ) : (
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-secondary text-[13px] font-[640] text-muted-foreground">
            {(need.poster?.display_name || "M").slice(0, 1)}
          </div>
        )}
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            {profileHref(need.poster) ? (
              <Link to={profileHref(need.poster)} className="text-sm font-[640] text-foreground hover:underline"
                data-testid={`community-need-poster-${need.delivery_id}`}>
                {need.poster?.display_name || "Member"}
              </Link>
            ) : (
              <span className="text-sm font-[640] text-foreground">{need.poster?.display_name || "Member"}</span>
            )}
            <span className="rounded bg-secondary px-1.5 py-0.5 text-[10px] font-[600] uppercase tracking-wide text-muted-foreground">
              {need.poster?.profile_type || "personal"}
            </span>
            {need.is_new && (
              <span className="rounded-full bg-teal-600 px-2 py-0.5 text-[10px] font-[700] uppercase tracking-wide text-white"
                data-testid={`community-need-new-${need.delivery_id}`}>New</span>
            )}
            {need.is_updated && (
              <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-[700] uppercase tracking-wide text-amber-800"
                data-testid={`community-need-updated-${need.delivery_id}`}>Updated</span>
            )}
          </div>
          <p className="mt-2 whitespace-pre-line text-sm text-foreground/90"
            data-testid={`community-need-summary-${need.delivery_id}`}>{need.summary}</p>

          <div className="mt-2.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-muted-foreground">
            <span className="inline-flex items-center gap-1 font-[600] text-teal-800">
              <Sparkles className="h-3.5 w-3.5" /> Otter Assist
            </span>
            {need.urgency && (
              <span data-testid={`community-need-urgency-${need.delivery_id}`}>
                {URGENCY_COPY[need.urgency] || need.urgency}
              </span>
            )}
            <span data-testid={`community-need-deadline-${need.delivery_id}`}>
              {need.deadline_at ? `By ${new Date(need.deadline_at).toLocaleString()}` : "No firm deadline"}
            </span>
          </div>

          {need.why_you_matched && (
            <div className="mt-2.5 rounded-md border border-yellow-300/70 bg-yellow-50 px-2.5 py-2"
              data-testid={`community-need-why-${need.delivery_id}`}>
              <div className="text-[10px] font-[700] uppercase tracking-wider text-teal-800">Why you matched</div>
              <p className="mt-0.5 text-[12px] text-foreground/90">{need.why_you_matched}</p>
            </div>
          )}

          <div className="mt-3 flex flex-wrap items-center gap-2">
            {need.post_id && (
              <Button asChild size="sm" variant="outline" className="h-7 text-[11px]" onClick={markReviewed}>
                <Link to={needHref(need.assist_id, "community")}
                  data-testid={`community-need-view-need-${need.delivery_id}`}>
                  <HandHeart className="mr-1 h-3.5 w-3.5" /> View Need
                </Link>
              </Button>
            )}
            {need.post_id && (
              <Button asChild size="sm" variant="outline" className="h-7 text-[11px]" onClick={markReviewed}>
                <Link to={postHref(need.post_id, "community")} data-testid={`community-need-view-post-${need.delivery_id}`}>
                  <ExternalLink className="mr-1 h-3.5 w-3.5" /> View Post
                </Link>
              </Button>
            )}
            {need.assist_status === "active" && (
              <OtterAssistHelpButton assistId={need.assist_id} testIdSuffix={need.delivery_id}
                onChanged={invalidate} />
            )}
            {archived ? (
              <>
                <Button type="button" size="sm" variant="outline" disabled={busy}
                  className="h-7 text-[11px]" onClick={() => act.mutate("restore")}
                  data-testid={`community-need-restore-${need.delivery_id}`}>
                  <ArchiveRestore className="mr-1 h-3.5 w-3.5" /> Restore
                </Button>
                {need.response === "not_a_good_fit" && (
                  <Button type="button" size="sm" variant="ghost" disabled={busy}
                    className="h-7 px-2 text-[11px]"
                    onClick={() => dispose.mutate("undo_not_a_good_fit")}
                    data-testid={`community-need-undo-nagf-${need.delivery_id}`}>
                    <Undo2 className="mr-1 h-3.5 w-3.5" /> Undo Not a Good Fit
                  </Button>
                )}
              </>
            ) : (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button type="button" size="sm" variant="ghost" disabled={busy}
                    className="ml-auto h-7 px-2 text-[11px]"
                    data-testid={`community-need-menu-${need.delivery_id}`}>
                    {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <MoreHorizontal className="h-4 w-4" />}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => act.mutate("archive")}
                    data-testid={`community-need-archive-${need.delivery_id}`}>
                    <Archive className="mr-2 h-3.5 w-3.5" /> Archive
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => dispose.mutate("not_right_now")}
                    data-testid={`community-need-not-right-now-${need.delivery_id}`}>
                    Not right now
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => dispose.mutate("not_a_good_fit")}
                    data-testid={`community-need-not-a-good-fit-${need.delivery_id}`}>
                    Not a good fit
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </div>
      </div>
    </article>
  );
}

export default CommunityNeedCard;
